import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  FlexBox,
  FormDialog,
  Grid,
  MuiBox,
  MuiTable,
  SvgIcon,
  SvgIconButton,
  Tooltip,
  Typography
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { selectors as authSelectors } from 'store/auth';
import { actions as clientsActions, selectors as clientsSelectors, Client } from 'store/clients';
import { orDash } from 'utils/stringFormat';
import CreateCustomerOverlay from './components/CreateCustomerOverlay';
import EditCustomerOverlay from './components/EditCustomerOverlay';
import { useStyles } from './styled';
import { CustomerSortKey } from './types';
import { getIntegrationNames, getSortedClients } from './utils';

const CustomersView: React.FC = () => {
  const { deleteClient, getClients, resetAction, setClientId } = useActions({
    deleteClient: clientsActions.deleteClient,
    getClients: clientsActions.getClients,
    resetAction: clientsActions.resetAction,
    setClientId: clientsActions.setClientId
  });

  const authAdmin = useSelector(authSelectors.selectAdmin);
  const { success: clientSuccess, error: clientError } = useSelector(clientsSelectors.selectAction);
  const activeClient = useSelector(clientsSelectors.selectClient) as Client;
  const clients = useSelector(clientsSelectors.selectClients) as Client[];

  const [showCreateCustomerOverlay, setShowCreateCustomerOverlay] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [clientToEdit, setClientToEdit] = useState<Client | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sortParams, setSortParams] = useState<{ key: CustomerSortKey; order: 'asc' | 'desc' }>({
    key: 'name',
    order: 'asc'
  });

  const classes = useStyles();

  const integrationNames = useMemo(
    () =>
      clients.reduce<Record<string, string>>((acc, client) => {
        acc[client.id] = getIntegrationNames(client).join(', ');
        return acc;
      }, {}),
    [clients]
  );

  const sortedClients = useMemo(
    () => getSortedClients(clients, integrationNames, sortParams.key, sortParams.order),
    [clients, integrationNames, sortParams]
  );

  useEffect(() => {
    getClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (clientSuccess) {
      resetAction();
      setShowCreateCustomerOverlay(false);
      setClientToEdit(null);
      setClientToDelete(null);

      getClients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSuccess]);

  useEffect(() => {
    if (clientError) {
      if (clientToDelete) {
        setErrorMessage(`${clientToDelete.name} cannot be deleted at this time.`);
        setClientToDelete(null);
      } else {
        setErrorMessage(clientError);
      }

      resetAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientError]);

  const handleHeaderClick = (key: CustomerSortKey) => {
    setSortParams((prev) => ({
      key,
      order: prev.key === key && prev.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortArrow = () => {
    return (
      <Box sx={{ display: 'inline-block', cursor: 'pointer' }}>
        <SvgIcon name={sortParams.order === 'asc' ? 'drop_up' : 'drop_down'} size={15} style={{ marginLeft: '5px' }} />
      </Box>
    );
  };

  const onDeleteConfirmClick = () => {
    if (clientToDelete) {
      deleteClient({ id: clientToDelete.id });
    }
  };

  return (
    <MuiBox display="flex" flexDirection="column" height="100%" paddingBottom="24px">
      <Grid className={classes.headerContainer} data-testid="customers-page">
        <Box flexOverride="none">
          <Typography className={classes.header}>Customers</Typography>
        </Box>
        <Button customSize="small" width={175} onClick={() => setShowCreateCustomerOverlay(true)}>
          Create Customer
        </Button>
      </Grid>

      <MuiBox className={classes.tableOuterBox}>
        <Card hasBorderRadius raised fullHeight sx={{ flex: '1 0 0', display: 'flex', flexDirection: 'column' }}>
          <MuiTable.TableContainer id="userViewTable" sx={{ flex: 1, overflowY: 'auto' }}>
            <MuiTable.Table stickyHeader sx={{ borderCollapse: 'collapse' }}>
              <MuiTable.TableHead>
                <MuiTable.TableRow>
                  <MuiTable.TableCell
                    className={`${classes.nameTableCell} ${classes.sortableTableCell}`}
                    onClick={() => handleHeaderClick('name')}
                    data-testid="name-header"
                  >
                    Name
                    {sortParams.key === 'name' && <SortArrow />}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell
                    className={`${classes.integrationsTableCell} ${classes.sortableTableCell}`}
                    onClick={() => handleHeaderClick('integrations')}
                    data-testid="integrations-header"
                  >
                    Integrations
                    {sortParams.key === 'integrations' && <SortArrow />}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell className={classes.actionsTableCell}>Actions</MuiTable.TableCell>
                </MuiTable.TableRow>
              </MuiTable.TableHead>

              <MuiTable.TableBody>
                {sortedClients.map((client) => {
                  const isActive = activeClient?.id === client.id;
                  const isAssignedClient = authAdmin?.clientId === client.id;

                  return (
                    <MuiTable.TableRow
                      key={client.id}
                      className={clientToEdit?.id === client.id ? classes.selectedRow : undefined}
                    >
                      <MuiTable.TableCell className={classes.nameTableCell}>
                        <Typography fontSize={16}>{client.name}</Typography>
                      </MuiTable.TableCell>
                      <MuiTable.TableCell>
                        <Typography fontSize={16} data-testid="customer-integrations">
                          {orDash(integrationNames[client.id])}
                        </Typography>
                      </MuiTable.TableCell>
                      <MuiTable.TableCell>
                        <FlexBox justify="end" className={classes.actionIconsBox}>
                          {!isActive && (
                            <Tooltip title="Make Customer Active" arrow placement="top" verticalOffset={-8}>
                              <SvgIconButton
                                onClick={() => setClientId(client.id)}
                                data-testid="activate-customer-button"
                              >
                                <SvgIcon name="check_circle" />
                              </SvgIconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Edit Customer" arrow placement="top" verticalOffset={-8}>
                            <SvgIconButton onClick={() => setClientToEdit(client)} data-testid="update-customer-button">
                              <SvgIcon name="edit_circle" />
                            </SvgIconButton>
                          </Tooltip>
                          {!isActive && !isAssignedClient && (
                            <Tooltip title="Delete Customer" arrow placement="top" verticalOffset={-8}>
                              <SvgIconButton
                                onClick={() => setClientToDelete(client)}
                                data-testid="delete-customer-button"
                              >
                                <SvgIcon name="exit_circle" />
                              </SvgIconButton>
                            </Tooltip>
                          )}
                        </FlexBox>
                      </MuiTable.TableCell>
                    </MuiTable.TableRow>
                  );
                })}
              </MuiTable.TableBody>
            </MuiTable.Table>
          </MuiTable.TableContainer>
        </Card>
      </MuiBox>
      <Grid className={classes.footerContainer}>
        <p className={classes.total}>Customers: {clients.length}</p>
      </Grid>

      {/* CREATE OVERLAY */}
      {showCreateCustomerOverlay && <CreateCustomerOverlay onClose={() => setShowCreateCustomerOverlay(false)} />}

      {/* EDIT OVERLAY */}
      {clientToEdit && <EditCustomerOverlay client={clientToEdit} onClose={() => setClientToEdit(null)} />}

      {/* DELETE OVERLAY */}
      {clientToDelete && (
        <FormDialog
          cancelButtonAction={() => setClientToDelete(null)}
          cancelButtonText="Cancel"
          confirmButtonAction={() => onDeleteConfirmClick()}
          confirmButtonText="Delete"
          open
        >
          <Typography fontSize={24} maxWidth="524px">
            Are you sure you want to delete this customer?
          </Typography>
        </FormDialog>
      )}

      {/* ERROR OVERLAY */}
      {errorMessage && (
        <FormDialog
          cancelButtonAction={() => setErrorMessage(null)}
          confirmButtonAction={() => setErrorMessage(null)}
          confirmButtonText="Ok"
          open
        >
          <Typography fontSize={24} maxWidth="524px">
            {errorMessage}
          </Typography>
        </FormDialog>
      )}
    </MuiBox>
  );
};

export default CustomersView;
